import { useState } from 'react';
import { Loader2, Search, CheckCircle2, XCircle, AlertTriangle } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { diagnosticsAPI } from '../../lib/api';
import { toast } from 'sonner';
import { getErrorMessage } from '../../lib/errors';


function VerdictBanner({ data }) {
  if (!data) return null;

  const { verdict, tenants } = data;

  if (verdict === 'found') {
    const hit = tenants.find((t) => t.status === 'found');
    const field = hit?.invoice_number_match ? 'InvoiceNumber' : 'Reference';
    return (
      <div className="rounded border border-green-300 bg-green-50 dark:bg-green-950/30 dark:border-green-800 px-4 py-3">
        <div className="flex items-start gap-2">
          <CheckCircle2 className="h-5 w-5 text-green-600 shrink-0 mt-0.5" />
          <div>
            <p className="text-sm font-semibold text-green-800 dark:text-green-300">
              FOUND in {hit?.tenant_name} via {field}
            </p>
            <p className="text-xs text-green-700 dark:text-green-400 mt-0.5">
              Xero InvoiceID: <span className="font-mono">{hit?.xero_invoice_id}</span>
            </p>
          </div>
        </div>
      </div>
    );
  }

  if (verdict === 'not_found') {
    const tenantCount = tenants.length;
    return (
      <div className="rounded border border-red-300 bg-red-50 dark:bg-red-950/30 dark:border-red-800 px-4 py-3">
        <div className="flex items-start gap-2">
          <XCircle className="h-5 w-5 text-red-600 shrink-0 mt-0.5" />
          <div>
            <p className="text-sm font-semibold text-red-800 dark:text-red-300">
              NOT FOUND in any of {tenantCount} tenant{tenantCount !== 1 ? 's' : ''}
            </p>
            <p className="text-xs text-red-700 dark:text-red-400 mt-0.5">
              Searched InvoiceNumber and Reference, raw and normalized
            </p>
          </div>
        </div>
      </div>
    );
  }

  // inconclusive
  const errorCount = tenants.filter((t) => t.status === 'error').length;
  return (
    <div className="rounded border border-amber-300 bg-amber-50 dark:bg-amber-950/30 dark:border-amber-800 px-4 py-3">
      <div className="flex items-start gap-2">
        <AlertTriangle className="h-5 w-5 text-amber-600 shrink-0 mt-0.5" />
        <div>
          <p className="text-sm font-semibold text-amber-800 dark:text-amber-300">
            INCONCLUSIVE
          </p>
          <p className="text-xs text-amber-700 dark:text-amber-400 mt-0.5">
            {data.error
              ? data.error
              : `${errorCount} tenant(s) errored, see table`}
          </p>
        </div>
      </div>
    </div>
  );
}


function TenantStatusCell({ status, error }) {
  if (status === 'found') {
    return (
      <span className="inline-flex items-center gap-1 text-green-700 dark:text-green-400 text-xs font-medium">
        <CheckCircle2 className="h-3 w-3" /> found
      </span>
    );
  }
  if (status === 'not_found') {
    return (
      <span className="inline-flex items-center gap-1 text-muted-foreground text-xs">
        <XCircle className="h-3 w-3" /> not found
      </span>
    );
  }
  return (
    <span className="inline-flex items-center gap-1 text-red-600 dark:text-red-400 text-xs" title={error || ''}>
      <AlertTriangle className="h-3 w-3" /> error
      {error && <span className="text-[10px] ml-1 truncate max-w-[200px]">({error})</span>}
    </span>
  );
}


export function XeroInvoiceLookupPanel() {
  const [number, setNumber] = useState('INV-00191');
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);

  const runLookup = async () => {
    if (!number.trim()) return;
    setLoading(true);
    setError(null);
    setData(null);
    try {
      const { data: d } = await diagnosticsAPI.xeroInvoiceLookup(number.trim());
      setData(d);
    } catch (err) {
      setError(getErrorMessage(err));
      toast.error(getErrorMessage(err));
    } finally {
      setLoading(false);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter') runLookup();
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-lg flex items-center gap-2">
          <Search className="h-5 w-5" />
          Xero Invoice Live Lookup
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <p className="text-xs text-muted-foreground">
          Search all connected Xero tenants for a single invoice by InvoiceNumber
          and Reference (raw and normalized). Report-only, no DB writes.
        </p>

        <div className="flex items-center gap-2">
          <Input
            value={number}
            onChange={(e) => setNumber(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder="e.g. INV-00191"
            className="max-w-xs font-mono text-sm"
          />
          <Button
            variant="outline"
            size="sm"
            onClick={runLookup}
            disabled={loading || !number.trim()}
          >
            {loading ? <Loader2 className="h-3.5 w-3.5 animate-spin mr-1.5" /> : null}
            Look up
          </Button>
        </div>

        {error && (
          <div className="flex items-center gap-2 text-sm text-red-500">
            <AlertTriangle className="h-4 w-4" />
            {error}
          </div>
        )}

        {data && (
          <div className="space-y-4">
            <VerdictBanner data={data} />

            {/* Searched values */}
            <div className="grid grid-cols-2 gap-2 text-xs">
              <div>
                <span className="text-muted-foreground">Raw:</span>{' '}
                <span className="font-mono">{data.number_searched}</span>
              </div>
              <div>
                <span className="text-muted-foreground">Normalized:</span>{' '}
                <span className="font-mono">{data.number_normalized}</span>
              </div>
            </div>

            {/* Per-tenant table */}
            {data.tenants && data.tenants.length > 0 && (
              <div className="overflow-x-auto">
                <table className="w-full text-xs">
                  <thead>
                    <tr className="border-b text-left">
                      <th className="py-1.5 pr-3 font-medium text-muted-foreground">Tenant</th>
                      <th className="py-1.5 pr-3 font-medium text-muted-foreground">InvoiceNumber</th>
                      <th className="py-1.5 pr-3 font-medium text-muted-foreground">Reference</th>
                      <th className="py-1.5 pr-3 font-medium text-muted-foreground">Xero InvoiceID</th>
                      <th className="py-1.5 font-medium text-muted-foreground">Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.tenants.map((t, i) => (
                      <tr key={t.tenant_id || i} className="border-b last:border-0">
                        <td className="py-1.5 pr-3">{t.tenant_name}</td>
                        <td className="py-1.5 pr-3 font-mono">
                          {t.invoice_number_match || <span className="text-muted-foreground">-</span>}
                        </td>
                        <td className="py-1.5 pr-3 font-mono">
                          {t.reference_match || <span className="text-muted-foreground">-</span>}
                        </td>
                        <td className="py-1.5 pr-3 font-mono text-[10px]">
                          {t.xero_invoice_id || <span className="text-muted-foreground">-</span>}
                        </td>
                        <td className="py-1.5">
                          <TenantStatusCell status={t.status} error={t.error} />
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}

            {data.tenants && data.tenants.length === 0 && (
              <p className="text-xs text-muted-foreground">No connected tenants.</p>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
}
